import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Target, Calendar as CalIcon, Sparkles, ShieldCheck, LineChart, Users, Radar, Image as ImageIcon, Wand2, Clock, Wrench, Layers, Mail, Globe2, Megaphone, Play, CheckCircle2, Brain, Workflow, Gauge, Bot } from "lucide-react";
import LiveDemoWidget from "@/components/LiveDemoWidget";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { useCurrency } from "@/context/CurrencyContext";
import { useTr } from "@/i18n/translator";

const Mark = ({ children }) => (
  <span className="font-mono-mark text-[11px] text-[#5C5C5C]">{children}</span>
);

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { format } = useCurrency();
  const tr = useTr();

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPlans(data.plans || []));
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#F4F4F0] border-b border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src="/logo-cia.png" alt="CIA Marketing" className="w-10 h-10 object-contain" />
            <span className="font-display tracking-tighter text-lg">CIA Marketing<span className="text-[#E53935]">®</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 font-mono-mark text-[11px]">
            <a href="#autopilot" className="hover:text-[#E53935] text-[#E53935]" data-testid="nav-autopilot">AUTOPILOT · NUEVO</a>
            <a href="#features" className="hover:text-[#E53935]" data-testid="nav-features">{t("nav.features").toUpperCase()}</a>
            <a href="#smart" className="hover:text-[#E53935]" data-testid="nav-smart">IA</a>
            <a href="#capabilities" className="hover:text-[#E53935]" data-testid="nav-capabilities">{t("landing.nav_capabilities").toUpperCase()}</a>
            <a href="#demo" className="hover:text-[#E53935]" data-testid="nav-demo">{t("nav.demo").toUpperCase()}</a>
            <a href="#brand-service" className="hover:text-[#E53935]" data-testid="nav-brand">{t("nav.brand_service").toUpperCase()}</a>
            <a href="#pricing" className="hover:text-[#E53935]" data-testid="nav-pricing">{t("nav.pricing").toUpperCase()}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <CurrencySwitcher />
            <Link to="/login" className="hidden sm:inline-block font-mono-mark text-[11px] px-3 py-2 border border-transparent hover:border-[#0A0A0A]" data-testid="header-login">
              {t("common.login").toUpperCase()}
            </Link>
            <Link to="/register" className="font-mono-mark text-[11px] bg-[#0A0A0A] text-white px-4 py-2 hover-brutal border border-[#0A0A0A]" data-testid="header-register">
              {t("common.register").toUpperCase()}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-[#0A0A0A] overflow-hidden">
        <div className="dossier-grid absolute inset-0 opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[#0A0A0A] bg-white px-3 py-1 mb-8">
              <span className="w-2 h-2 bg-[#E53935] rounded-full" />
              <Mark>{t("landing.hero_classified").toUpperCase()}</Mark>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              {t("landing.hero_pre")}<span className="bg-[#FFD600] px-2 inline-block">{t("landing.hero_highlight")}</span><br />
              {t("landing.hero_mid")}<span className="text-[#E53935]">{t("landing.hero_accent")}</span>
            </h1>
            <p className="mt-8 max-w-xl text-[#0A0A0A]/80 text-base leading-relaxed">
              CIA Marketing<span className="text-[#E53935]">®</span> · {t("landing.hero_sub_body")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={() => navigate("/register")}
                className="group inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-6 py-4 font-mono-mark text-xs hover-brutal border border-[#0A0A0A]"
                data-testid="hero-cta-register"
              >
                {t("landing.cta_free").toUpperCase()}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate("/pricing")}
                className="inline-flex items-center gap-3 bg-transparent text-[#0A0A0A] px-6 py-4 font-mono-mark text-xs border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                data-testid="hero-cta-pricing"
              >
                {t("landing.cta_pricing").toUpperCase()}
              </button>
            </div>
            <div className="mt-12 flex flex-wrap gap-6 text-xs font-mono-data text-[#5C5C5C]">
              <div><span className="text-[#0A0A0A] font-bold">{t("landing.hero_stat1_value")}</span> {t("landing.hero_stat1_label").toUpperCase()}</div>
              <div><span className="text-[#0A0A0A] font-bold">{t("landing.hero_stat2_value")}</span> {t("landing.hero_stat2_label").toUpperCase()}</div>
              <div><span className="text-[#0A0A0A] font-bold">{t("landing.hero_stat3_value")}</span> {t("landing.hero_stat3_label").toUpperCase()}</div>
            </div>
          </div>

          {/* Dossier Card */}
          <div className="md:col-span-5">
            <div className="bg-white border border-[#0A0A0A] brutal-shadow p-6">
              <div className="flex items-center justify-between border-b border-[#0A0A0A] pb-3">
                <Mark>{t("landing.dossier_objective_mark").toUpperCase()}</Mark>
                <Mark className="text-[#E53935]">{t("landing.dossier_objective_status").toUpperCase()}</Mark>
              </div>
              <div className="py-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#0A0A0A] text-white font-mono-mark text-[10px] flex items-center justify-center shrink-0 mt-1">01</div>
                  <div>
                    <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{t("landing.dossier_generate").toUpperCase()}</div>
                    <div className="font-display text-lg leading-tight">{t("landing.dossier_generate_d")}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#0A0A0A] text-white font-mono-mark text-[10px] flex items-center justify-center shrink-0 mt-1">02</div>
                  <div>
                    <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{t("landing.dossier_schedule").toUpperCase()}</div>
                    <div className="font-display text-lg leading-tight">{t("landing.dossier_schedule_d")}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#E53935] text-white font-mono-mark text-[10px] flex items-center justify-center shrink-0 mt-1">03</div>
                  <div>
                    <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{t("landing.dossier_dominate").toUpperCase()}</div>
                    <div className="font-display text-lg leading-tight">{t("landing.dossier_dominate_d")}</div>
                  </div>
                </div>
              </div>
              <div className="border-t border-[#0A0A0A] pt-3 flex items-center justify-between">
                <Mark>{t("landing.dossier_clearance").toUpperCase()}</Mark>
                <ShieldCheck className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-[#FFD600] overflow-hidden">
        <div className="flex scroll-marquee whitespace-nowrap py-3 gap-12 font-mono-mark text-xs">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-12 shrink-0">
              {t("landing.ticker_list").split("·").map((item, j) => (
                <span key={j}>{item.trim().toUpperCase()}{j < t("landing.ticker_list").split("·").length - 1 ? " ·" : ""}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────── */}
      {/*  DIRECTOR DE MARKETING AUTÓNOMO — feature estrella          */}
      {/* ─────────────────────────────────────────────────────────── */}
      <section id="autopilot" className="border-b border-[#0A0A0A] bg-[#F4F4F0] relative overflow-hidden">
        {/* Grain texture */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply"
             style={{ backgroundImage: "radial-gradient(#0A0A0A 1px, transparent 1px)", backgroundSize: "4px 4px" }} />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 relative">
          {/* Header */}
          <div className="grid grid-cols-12 gap-6 items-end mb-16">
            <div className="col-span-12 lg:col-span-8">
              <div className="inline-flex items-center gap-2 bg-[#E53935] text-white font-mono-mark text-[10px] px-3 py-1.5">
                <Bot className="w-3 h-3" /> {tr("NUEVO · FLAGSHIP 2026")}
              </div>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[0.92] tracking-tight">
                {tr("Un Director de Marketing")}<br />
                <span className="italic">{tr("que nunca duerme.")}</span>
              </h2>
              <p className="font-mono-data text-base text-[#5C5C5C] mt-6 max-w-2xl leading-relaxed">
                {tr("Tú das el brief. CIA ejecuta. La primera IA que planea, genera, programa, publica y reporta campañas multi-red — sin un human@ tocando un botón.")}
              </p>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <div className="bg-[#0A0A0A] text-white p-6 border-l-4 border-[#FFD600]">
                <div className="font-mono-mark text-[10px] text-[#FFD600]">{tr("PRECIO DE MERCADO")}</div>
                <div className="font-display text-3xl mt-2 line-through text-white/40">$500 USD</div>
                <div className="font-mono-mark text-[10px] text-[#FFD600] mt-3">{tr("INCLUIDO EN TU PLAN CON VIDEO")}</div>
                <div className="font-display text-5xl mt-1">$0 extra</div>
                <div className="font-mono-data text-xs text-white/60 mt-1">{tr("sin costos por campaña · sin límite de piezas")}</div>
              </div>
            </div>
          </div>

          {/* Cómo funciona — 5 pasos en columna de bloques */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-0 border border-[#0A0A0A] bg-white mb-16">
            {[
              { n: "01", icon: Brain, t: tr("Briefing IA"),       d: tr("5 preguntas: marca, objetivo, tono, audiencia y geo. La IA entiende tu contexto completo.") },
              { n: "02", icon: Workflow, t: tr("Plan generado"),  d: tr("Planifica cada pieza por red y hora pico, distribuida en los días que dures la campaña.") },
              { n: "03", icon: Sparkles, t: tr("Contenido real"), d: tr("IA redacta copy, ilustra imágenes y graba video vertical. Todo listo 30 min antes de publicar.") },
              { n: "04", icon: Megaphone, t: tr("Publicación multi-red"), d: tr("Instagram, Facebook, X, LinkedIn, TikTok — cada post sale a su mejor hora con las credenciales de la marca.") },
              { n: "05", icon: Gauge, t: tr("Reporte final IA"), d: tr("Al cierre recibes análisis vs competencia, engagement y recomendaciones para la siguiente campaña.") },
            ].map((step, i) => (
              <div key={step.n} className={`p-6 md:p-8 ${i < 4 ? "md:border-r border-[#0A0A0A]" : ""} border-b md:border-b-0 border-[#0A0A0A] last:border-b-0`}>
                <div className="flex items-center justify-between">
                  <span className="font-display text-4xl leading-none text-[#E53935]">{step.n}</span>
                  <step.icon className="w-5 h-5 text-[#0A0A0A]" />
                </div>
                <h3 className="font-display text-xl mt-5 leading-tight">{step.t}</h3>
                <p className="font-mono-data text-xs text-[#5C5C5C] mt-3 leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>

          {/* Split: mockup + bondades */}
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {/* Lado A: Mockup */}
            <div className="col-span-12 lg:col-span-7">
              <div className="bg-[#0A0A0A] text-white border border-[#0A0A0A] overflow-hidden">
                <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#E53935]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FFD600]" />
                    <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                  </div>
                  <span className="font-mono-mark text-[9px] text-white/40">/dashboard/campaigns/black-friday-2026</span>
                </div>
                <div className="p-6 md:p-8 space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-mono-mark text-[9px] text-[#FFD600]">BLACK FRIDAY 2026 · VENTAS · AGRESIVO</div>
                      <div className="font-display text-3xl mt-1">Campaña Activa</div>
                    </div>
                    <span className="font-mono-mark text-[9px] bg-[#FFD600] text-[#0A0A0A] px-2 py-1">14 / 30 DÍAS</span>
                  </div>
                  <div className="h-1 bg-white/10">
                    <div className="h-full bg-[#E53935]" style={{ width: "47%" }} />
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-center">
                    {[
                      { k: "PUBLICADOS", v: "73" },
                      { k: "PROGRAMADOS", v: "82" },
                      { k: "VIEWS", v: "142K" },
                      { k: "ENGAGEMENT", v: "4.8%" },
                    ].map((kpi) => (
                      <div key={kpi.k} className="border border-white/10 p-3">
                        <div className="font-mono-mark text-[8px] text-white/50">{kpi.k}</div>
                        <div className="font-display text-2xl mt-1">{kpi.v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-5 gap-2 pt-2">
                    {["IG","FB","X","IN","TT"].map((net) => (
                      <div key={net} className="border border-white/10 px-3 py-2 flex items-center justify-between">
                        <span className="font-mono-mark text-[10px] text-white/70">{net}</span>
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#FFD600]" />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                    <div className="w-2 h-2 rounded-full bg-[#FFD600] animate-pulse" />
                    <span className="font-mono-data text-[11px] text-white/60">
                      {tr("Generando post 74 · Instagram · próxima publicación en 17 min")}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Lado B: Bondades */}
            <div className="col-span-12 lg:col-span-5 space-y-4">
              <h3 className="font-display text-3xl leading-tight">
                {tr("No es un generador. Es tu equipo de marketing operando 24/7.")}
              </h3>
              {[
                { t: tr("100% autónomo"),      d: tr("Cierras el navegador. CIA sigue generando y publicando. Tú revisas cuando quieras.") },
                { t: tr("Multi-marca, multi-red"), d: tr("Cada marca con su voz, su tono, sus hashtags y sus mejores horarios — sin contaminación de datos.") },
                { t: tr("Duración 100% flexible"), d: tr("7 días, 30, 60 o 365. Cadencia configurable por red: 1 post, 3 imágenes y 1 video al día si quieres.") },
                { t: tr("Respeta tu presupuesto IA"), d: tr("Generación por lotes, video solo si tu plan lo incluye. Sin cargos extra por campaña.") },
                { t: tr("Reporte de cierre con IA"), d: tr("Análisis vs competencia, engagement por red, mejor hora real y recomendaciones para la próxima campaña.") },
              ].map((b, i) => (
                <div key={i} className="group flex gap-4 p-4 bg-white border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors">
                  <CheckCircle2 className="w-5 h-5 shrink-0 text-[#E53935] group-hover:text-[#FFD600] mt-0.5" />
                  <div>
                    <div className="font-display text-lg leading-tight">{b.t}</div>
                    <div className="font-mono-data text-xs text-[#5C5C5C] group-hover:text-white/70 mt-1">{b.d}</div>
                  </div>
                </div>
              ))}
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-[#E53935] text-white font-mono-mark text-[11px] px-6 py-4 hover:bg-[#0A0A0A] mt-2"
                data-testid="autopilot-cta"
              >
                <Play className="w-4 h-4" /> {tr("ACTIVAR MI DIRECTOR DE MARKETING")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section id="features" className="border-b border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <Mark>{t("landing.features_block_mark").toUpperCase()}</Mark>
              <h2 className="font-display text-4xl sm:text-5xl mt-3 max-w-2xl leading-none">
                {t("landing.features_block_title")}
              </h2>
            </div>
            <div className="font-mono-data text-sm text-[#5C5C5C] max-w-sm">
              {t("landing.capabilities_sub")}
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 md:gap-8">
            <FeatureCard size="lg" icon={<Sparkles />} title={t("landing.f_generator_t")} kicker={t("landing.f_generator_k").toUpperCase()}>
              {t("landing.f_generator_d")}
            </FeatureCard>
            <FeatureCard size="md" icon={<ImageIcon />} title={t("landing.f_images_t")} kicker={t("landing.f_images_k").toUpperCase()}>
              {t("landing.f_images_d")}
            </FeatureCard>
            <FeatureCard size="md" icon={<CalIcon />} title={t("landing.f_calendar_t")} kicker={t("landing.f_calendar_k").toUpperCase()}>
              {t("landing.f_calendar_d")}
            </FeatureCard>
            <FeatureCard size="lg" icon={<LineChart />} title={t("landing.f_analytics_t")} kicker={t("landing.f_analytics_k").toUpperCase()}>
              {t("landing.f_analytics_d")}
            </FeatureCard>
            <FeatureCard size="sm" icon={<Radar />} title={t("landing.f_competitors_t")} kicker={t("landing.f_competitors_k").toUpperCase()} />
            <FeatureCard size="sm" icon={<Target />} title={t("landing.f_listening_t")} kicker={t("landing.f_listening_k").toUpperCase()} />
            <FeatureCard size="sm" icon={<Users />} title={t("landing.f_bank_t")} kicker={t("landing.f_bank_k").toUpperCase()} />
          </div>
        </div>
      </section>

      {/* AI Smart Features — bondades específicas de la IA */}
      <section id="smart" className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: "repeating-linear-gradient(45deg, #FFD600 0 1px, transparent 1px 12px)" }} />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 relative">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <span className="font-mono-mark text-[11px] text-[#FFD600]">CIA · IA INTELLIGENCE</span>
              <h2 className="font-display text-4xl sm:text-5xl mt-3 max-w-3xl leading-none">
                {tr("Tu agencia con cerebro propio.")}
              </h2>
              <p className="font-mono-data text-sm text-white/60 mt-4 max-w-2xl">
                {tr("Mientras los demás te dejan al frente del calendario, CIA Marketing aprende cómo escribe tu marca, a qué hora rinde mejor cada red, y arregla problemas operativos antes de que los reportes lleguen a tu bandeja.")}
              </p>
            </div>
            <span className="font-mono-mark text-[10px] bg-[#FFD600] text-[#0A0A0A] px-3 py-1.5">
              {tr("NUEVO · 2026")}
            </span>
          </div>

          <div className="grid grid-cols-12 gap-6 md:gap-8">
            {/* A/B testing */}
            <SmartCard size="lg" tag={tr("A/B TESTING IA")} icon={<Wand2 className="w-5 h-5" />}
                       title={tr("Dos variantes contrastantes en cada generación.")}>
              {tr("Activas el modo A/B y la IA produce 2 textos con estilos opuestos (directo vs narrativo). Eliges la que prefieres y la marca aprende: a partir del 4º post, te marca con un sello \"RECOMENDADA\" el estilo que tu cliente ya validó.")}
            </SmartCard>

            {/* Best time */}
            <SmartCard size="md" tag={tr("BEST TIME · CRON")} icon={<Clock className="w-5 h-5" />}
                       title={tr("Publica cuando tu audiencia está despierta, no cuando tú te acuerdas.")}>
              {tr("Mientras no haya datos propios, usamos los slots óptimos de la industria (Instagram → Martes 7 PM, LinkedIn → Martes 9 AM…). En cuanto acumulas 5 posts publicados, basculamos a tu data real: el botón \"MEJOR HORA\" programa al próximo slot top de tu marca.")}
            </SmartCard>

            {/* Auto-Repair */}
            <SmartCard size="md" tag={tr("AUTO-REPAIR · TICKETS")} icon={<Wrench className="w-5 h-5" />}
                       title={tr("Tickets que se arreglan solos, sin esperar a soporte.")}>
              {tr("La IA clasifica cada ticket en data / config / user_error / code. Si es seguro (limpiar lock de fuerza bruta, cambiar idioma, reactivar usuario) ejecuta el fix sobre la base de datos en segundos. Sólo los bugs que requieren tocar código escalan a un humano.")}
            </SmartCard>

            {/* Multi-brand */}
            <SmartCard size="lg" tag={tr("MULTI-BRAND · AGENCIA")} icon={<Layers className="w-5 h-5" />}
                       title={tr("5 marcas, 5 voces, 5 calendarios — cero contaminación de datos.")}>
              {tr("Cada marca tiene su brand_config, sus cuentas sociales, sus posts, su analytics, su brand voice. La vista 360 de Agencia consolida lo que importa sin mezclar nada. Ideal para freelancers que llevan varios clientes o holdings con productos hijos.")}
            </SmartCard>

            {/* Brand Builder */}
            <SmartCard size="sm" tag={tr("BRAND BUILDER")} icon={<Sparkles className="w-5 h-5" />}
                       title={tr("De idea a marca en 9 pasos.")}>
              {tr("Logo generado con IA + slogan + paleta + landing HTML/CSS y e-commerce descargables en ZIP.")}
            </SmartCard>

            {/* Email */}
            <SmartCard size="sm" tag={tr("EMAIL · AUTOMATIZADO")} icon={<Mail className="w-5 h-5" />}
                       title={tr("Welcome, contratos y upsells reales.")}>
              {tr("Cron diario manda recordatorios de renewal y upsell free→pago. Idempotente, con log auditable.")}
            </SmartCard>

            {/* OAuth */}
            <SmartCard size="sm" tag={tr("OAUTH · NATIVO")} icon={<Globe2 className="w-5 h-5" />}
                       title={tr("Conecta IG, FB, LinkedIn, TikTok, X.")}>
              {tr("Tokens cifrados con AES, state firmado HMAC, refresh automático. Listo para escalar internacional.")}
            </SmartCard>

            {/* Live demo widget */}
            <LiveDemoWidget />
          </div>
        </div>
      </section>

      <section id="how" className="border-b border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <Mark>{t("landing.how_mark").toUpperCase()}</Mark>
          <h2 className="font-display text-4xl sm:text-5xl mt-3 mb-12">{t("landing.how_title")}</h2>
          <div className="grid md:grid-cols-4 gap-0 border border-[#0A0A0A]">
            {[
              ["01", t("landing.step_register_t"), t("landing.step_register_d")],
              ["02", t("landing.step_connect_t"), t("landing.step_connect_d")],
              ["03", t("landing.step_generate_t"), t("landing.step_generate_d")],
              ["04", t("landing.step_publish_t"), t("landing.step_publish_d")],
            ].map(([n, title, d], i) => (
              <div key={n} className={`p-6 md:p-8 ${i < 3 ? "md:border-r border-b md:border-b-0 border-[#0A0A0A]" : "border-b-0"}`}>
                <div className="font-display text-7xl text-[#E53935] leading-none">{n}</div>
                <div className="font-display text-xl mt-4">{title}</div>
                <div className="text-sm mt-2 text-[#5C5C5C] font-mono-data">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities — Todo lo que hace la plataforma */}
      <section id="capabilities" className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="dossier-grid absolute inset-0 opacity-10 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="inline-flex items-center gap-2 border border-white/30 bg-white/5 backdrop-blur px-3 py-1 mb-6">
                <span className="w-2 h-2 bg-[#FFD600] rounded-full animate-pulse" />
                <Mark>{t("landing.capabilities_mark").toUpperCase()}</Mark>
              </div>
              <h2 className="font-display text-4xl sm:text-6xl leading-[0.95] tracking-tighter max-w-3xl">
                {t("landing.capabilities_title_a")}<br />
                <span className="bg-[#FFD600] text-[#0A0A0A] px-2 inline-block">{t("landing.capabilities_title_highlight")}</span><br />
                {t("landing.capabilities_title_b")}
              </h2>
              <p className="mt-6 max-w-xl text-white/70 text-sm font-mono-data leading-relaxed">
                {t("landing.capabilities_sub")}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(() => {
              // returnObjects: true puede devolver un string si el idioma actual no
              // tiene la lista. En ese caso usamos el inglés como fallback.
              let list = t("capabilities_list", { returnObjects: true });
              if (!Array.isArray(list)) {
                list = t("capabilities_list", { returnObjects: true, lng: "en" });
              }
              if (!Array.isArray(list)) list = [];
              return list.map((c) => (
                <Cap key={c.n} n={c.n} cat={c.cat} t={c.t} d={c.d} highlight={c.highlight} emph={c.emph} />
              ));
            })()}
          </div>

          <div className="mt-14 flex flex-wrap gap-4 items-center">
            <Link to="/register" className="inline-flex items-center gap-3 bg-[#E53935] text-white px-6 py-4 font-mono-mark text-xs border border-[#E53935] hover-brutal" data-testid="capabilities-cta">
              {t("landing.capabilities_cta").toUpperCase()} <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="font-mono-mark text-[10px] text-white/60">{t("landing.capabilities_cta_note").toUpperCase()}</span>
          </div>
        </div>
      </section>

      {/* Demo Gallery */}
      <section id="demo" className="border-b border-[#0A0A0A] bg-[#F4F4F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <Mark>{tr("DOSSIER 03 · MUESTRAS")}</Mark>
              <h2 className="font-display text-4xl sm:text-5xl mt-3 max-w-2xl leading-none">
                {tr("Así se ve tu contenido en cada red.")}
              </h2>
            </div>
            <p className="font-mono-data text-sm text-[#5C5C5C] max-w-sm">
              {tr("Mockups reales de lo que la plataforma publica: video vertical, feed de Instagram, carrusel y post de LinkedIn.")}
            </p>
          </div>

          {/* Mockups grid: 3 device-style cards */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {/* TIKTOK / REELS phone mockup */}
            <div className="bg-[#0A0A0A] border border-[#0A0A0A] brutal-shadow overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/20">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#E53935] rounded-full animate-pulse" />
                  <span className="font-mono-mark text-[9px] text-white">TIKTOK · FOR YOU · 9:16</span>
                </div>
                <span className="font-mono-mark text-[9px] text-[#FFD600]">{tr("EN VIVO")}</span>
              </div>
              <div className="relative aspect-[9/16] bg-[#0A0A0A]">
                <video
                  poster="https://images.unsplash.com/photo-1509042239860-f550ce710b93?q=80&w=800&auto=format&fit=crop"
                  autoPlay muted loop playsInline
                  className="w-full h-full object-cover"
                >
                  <source src="/demos/demo-coffee.webm" type="video/webm" />
                  <source src="/demos/demo-coffee.mp4" type="video/mp4" />
                </video>
                {/* TikTok overlay UI */}
                <div className="absolute right-3 bottom-20 flex flex-col gap-4 items-center text-white">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">❤</div>
                    <span className="font-mono-mark text-[9px] mt-1">124K</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">💬</div>
                    <span className="font-mono-mark text-[9px] mt-1">3.2K</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">↗</div>
                    <span className="font-mono-mark text-[9px] mt-1">892</span>
                  </div>
                </div>
                <div className="absolute left-0 right-0 bottom-0 p-4 bg-gradient-to-t from-black to-transparent text-white">
                  <div className="font-mono-mark text-[10px]">@tumarca</div>
                  <p className="text-sm mt-1 leading-tight">{tr("Tu café del lunes no debería saber a lunes ☕ · Hecho en CDMX #coffee #cafedeespecialidad")}</p>
                </div>
              </div>
              <div className="px-3 py-2 font-mono-mark text-[9px] text-[#FFD600]">{tr("GENERADO · 8S · AUTO-PUBLICADO 09:32")}</div>
            </div>

            {/* INSTAGRAM post mockup */}
            <div className="bg-white border border-[#0A0A0A] brutal-shadow overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-[#0A0A0A]">
                <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-[#E53935] via-[#FFD600] to-[#E53935] p-[2px]">
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                    <img src="/logo-cia.png" alt="" className="w-5 h-5 object-contain" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="font-display text-sm leading-none">tumarca.co</div>
                  <div className="font-mono-mark text-[9px] text-[#5C5C5C] mt-0.5">{tr("CDMX · MÉXICO")}</div>
                </div>
                <span className="font-mono-mark text-[12px]">⋯</span>
              </div>
              <video
                poster="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?q=80&w=800&auto=format&fit=crop"
                autoPlay muted loop playsInline
                className="w-full aspect-square object-cover"
              >
                <source src="/demos/demo-fashion.webm" type="video/webm" />
                <source src="/demos/demo-fashion.mp4" type="video/mp4" />
              </video>
              <div className="px-3 py-3">
                <div className="flex gap-3 text-lg">♡ 💬 ↗</div>
                <div className="font-mono-data text-[11px] mt-2"><b>4,892</b> {tr("me gusta")}</div>
                <p className="text-sm mt-1 leading-snug"><b>tumarca.co</b> {tr("Tu próxima compra no es una decisión, es una señal. La lanzamos el viernes — activa las notificaciones 🔔")}</p>
                <div className="text-[#0A0A0A]/60 text-[11px] mt-1 font-mono-data">#nuevacolección #ecommerce #méxico · {tr("ver 42 comentarios")}</div>
              </div>
              <div className="px-3 py-2 border-t border-[#0A0A0A] font-mono-mark text-[9px] text-[#E53935]">{tr("GENERADO · APROBADO AUTO · PUBLICADO 18:00")}</div>
            </div>

            {/* LINKEDIN / TWITTER mockup */}
            <div className="bg-white border border-[#0A0A0A] brutal-shadow overflow-hidden flex flex-col">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-[#0A0A0A]">
                <div className="w-7 h-7 bg-[#0A66C2] rounded text-white flex items-center justify-center font-display text-xs">in</div>
                <div className="flex-1">
                  <div className="font-display text-sm leading-none">Tu Marca · SaaS</div>
                  <div className="font-mono-mark text-[9px] text-[#5C5C5C] mt-0.5">{tr("2,340 SEGUIDORES · HACE 2H")}</div>
                </div>
              </div>
              <div className="px-4 py-4 flex-1">
                <p className="text-sm leading-relaxed">
                  {tr("Llevamos 3 años automatizando operaciones para PyMEs alrededor del mundo. Un patrón se repite en el 84% de los casos:")}
                  <br /><br />
                  <b>{tr("No es falta de producto. Es falta de distribución constante.")}</b>
                  <br /><br />
                  {tr("Por eso dejamos de contratar community managers y empezamos a publicar 5× más con IA.")}
                  <br /><br />
                  {tr("¿Resultado?")} <span className="bg-[#FFD600] px-1">{tr("CPL -38% en 90 días.")}</span>
                </p>
                <div className="mt-3 font-mono-data text-[11px] text-[#0A66C2]">#marketing #saas #ai #pymes</div>
              </div>
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop" alt="" className="w-full aspect-[16/10] object-cover border-t border-[#0A0A0A]" />
              <div className="px-3 py-2 border-t border-[#0A0A0A] flex items-center justify-between">
                <div className="font-mono-data text-[11px] text-[#5C5C5C]">👍 {tr("892 · 104 comentarios · 67 republicaciones")}</div>
                <div className="font-mono-mark text-[9px] text-[#E53935]">{tr("GENERADO")}</div>
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-4 items-center">
            <Link
              to="/register"
              className="inline-flex items-center gap-3 bg-[#E53935] text-white px-6 py-4 font-mono-mark text-xs border border-[#E53935] hover-brutal"
              data-testid="demo-cta-register"
            >
              {tr("GENERAR MI PRIMER POST GRATIS")} <ArrowRight className="w-4 h-4" />
            </Link>
            <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("NO REQUIERE TARJETA · CANCELA CUANDO QUIERAS")}</span>
          </div>
        </div>
      </section>

      {/* Voces · Copy generado */}
      <section className="border-b border-[#0A0A0A] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
            <div>
              <Mark>{tr("DOSSIER 03.2 · VOCES")}</Mark>
              <h2 className="font-display text-4xl sm:text-5xl mt-3 max-w-2xl leading-none">
                {tr("El copy que enganchó a miles.")} <span className="text-[#E53935]">{tr("Generado por IA.")}</span>
              </h2>
            </div>
            <p className="font-mono-data text-sm text-[#5C5C5C] max-w-sm">
              {tr("Hooks, captions y scripts que ya están rompiendo métricas a nivel global. Todos salieron de un prompt de 2 líneas.")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <VoiceCard
              label={tr("TWITTER / X · 2.1K RT")}
              bg="bg-[#FFD600]"
              text={tr(`"Tu equipo de marketing duerme, tus ventas también. 😴\n\nNosotros automatizamos la primera parte. De la segunda te encargas tú. ☕"`)}
              tags="#marketingdigital #pymes #ai"
              stats={tr("287K impresiones · 4.8% engagement")}
            />
            <VoiceCard
              label={tr("TIKTOK · HOOK · 3.2M VIEWS")}
              bg="bg-[#0A0A0A] text-white"
              text={tr(`"Nadie te va a decir esto, pero el 80% de tu competencia ya publica con IA…\n\nMientras tú sigues pagándole a 3 personas."`)}
              tags="#emprendedor #negocio #ia"
              stats={tr("3.2M vistas · +14K seguidores")}
              dark
            />
            <VoiceCard
              label={tr("INSTAGRAM · CAPTION")}
              bg="bg-[#F4F4F0]"
              text={tr(`"Sabemos lo que se siente. Abrir el feed un lunes a las 8am y recordar que olvidaste programar el post del viernes pasado.\n\nYa no más. 💛"`)}
              tags="#freelance #creators #herramientas"
              stats={tr("12,400 me gusta · +892 guardados")}
            />
          </div>
        </div>
      </section>

      {/* ¿No tienes marca? · Branding Service */}
      <section id="brand-service" className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="dossier-grid absolute inset-0 opacity-20 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-white/30 bg-white/5 backdrop-blur px-3 py-1 mb-6">
              <span className="w-2 h-2 bg-[#FFD600] rounded-full animate-pulse" />
              <Mark>{t("landing.brand_mark").toUpperCase()}</Mark>
            </div>
            <h2 className="font-display text-4xl sm:text-6xl leading-[0.95] tracking-tighter">
              {t("landing.brand_title_a")}<br />
              <span className="bg-[#FFD600] text-[#0A0A0A] px-2 inline-block">{t("landing.brand_title_b")}</span>
            </h2>
            <p className="mt-8 max-w-xl text-white/80 text-base leading-relaxed">
              {t("landing.brand_sub")}
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4 max-w-lg">
              <BrandIncl n="01" t={t("landing.brand_incl_logo")} d={t("landing.brand_incl_logo_d")} />
              <BrandIncl n="02" t={t("landing.brand_incl_slogan")} d={t("landing.brand_incl_slogan_d")} />
              <BrandIncl n="03" t={t("landing.brand_incl_palette")} d={t("landing.brand_incl_palette_d")} />
              <BrandIncl n="04" t={t("landing.brand_incl_typo")} d={t("landing.brand_incl_typo_d")} />
              <BrandIncl n="05" t={t("landing.brand_incl_guide")} d={t("landing.brand_incl_guide_d")} />
              <BrandIncl n="06" t={t("landing.brand_incl_voice")} d={t("landing.brand_incl_voice_d")} />
              <BrandIncl n="07" t={t("landing.brand_incl_web")} d={t("landing.brand_incl_web_d")} />
              <BrandIncl n="08" t={t("landing.brand_incl_ecom")} d={t("landing.brand_incl_ecom_d")} />
            </div>

            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <Link
                to="/brand-service"
                className="inline-flex items-center gap-3 bg-[#FFD600] text-[#0A0A0A] px-6 py-4 font-mono-mark text-xs border border-[#FFD600] hover-brutal"
                data-testid="brand-service-cta"
              >
                {t("landing.brand_cta").toUpperCase()} <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="font-mono-mark text-[10px] text-white/60">{t("landing.brand_cta_note").toUpperCase()}</span>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="bg-white text-[#0A0A0A] border border-[#FFD600] brutal-shadow p-6">
              <div className="flex items-center justify-between border-b border-[#0A0A0A] pb-3">
                <Mark>{t("landing.brand_price_mark").toUpperCase()}</Mark>
                <Mark className="text-[#E53935]">{t("landing.brand_price_region")}</Mark>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-6xl leading-none" data-testid="brand-price-base">{format(299)}</span>
                <span className="font-mono-mark text-[10px]">USD · {t("landing.brand_price_unit").toUpperCase()}</span>
              </div>
              <div className="mt-2 font-mono-data text-xs text-[#5C5C5C]">
                <s>{format(890)}</s> · {t("landing.brand_price_launch_sub")}
              </div>

              {/* E-commerce add-on highlight */}
              <div className="mt-5 bg-[#FFD600] border border-[#0A0A0A] p-3" data-testid="landing-brand-ecom-row">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-mono-mark text-[9px] text-[#0A0A0A]">{t("landing.brand_price_ecom_label").toUpperCase()}</div>
                    <div className="font-display text-base leading-tight mt-0.5">+{format(29)}</div>
                    <div className="font-mono-data text-[11px] text-[#0A0A0A]/70">{t("landing.brand_price_ecom_desc")}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-mark text-[9px] text-[#E53935]">{t("landing.brand_price_total_label").toUpperCase()}</div>
                    <div className="font-display text-3xl leading-none mt-0.5" data-testid="brand-price-total">{format(328)}</div>
                    <div className="font-mono-mark text-[9px] text-[#0A0A0A]/70">USD</div>
                  </div>
                </div>
              </div>

              <ul className="mt-6 space-y-2 text-sm font-mono-data">
                <li className="flex gap-2"><span className="text-[#E53935]">+</span>{t("landing.brand_incl_kickoff")}</li>
                <li className="flex gap-2"><span className="text-[#E53935]">+</span>{t("landing.brand_incl_revisions")}</li>
                <li className="flex gap-2"><span className="text-[#E53935]">+</span>{t("landing.brand_incl_files")}</li>
                <li className="flex gap-2"><span className="text-[#E53935]">+</span>{t("landing.brand_incl_website")}</li>
                <li className="flex gap-2"><span className="text-[#E53935]">+</span>{t("landing.brand_incl_pro")}</li>
                <li className="flex gap-2"><span className="text-[#E53935]">+</span>{t("landing.brand_incl_warranty")}</li>
              </ul>
              <div className="mt-6 pt-4 border-t border-[#0A0A0A] font-mono-mark text-[10px] text-[#5C5C5C]">
                {t("landing.brand_price_payment_note").toUpperCase()}
              </div>
            </div>
          </div>

          {/* Antes / Después — Mini galería */}
          <div className="md:col-span-12 mt-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <Mark>{t("landing.cases_mark").toUpperCase()}</Mark>
                <h3 className="font-display text-3xl sm:text-4xl mt-2 text-white">{t("landing.cases_title")}</h3>
              </div>
              <p className="font-mono-data text-xs text-white/60 max-w-sm">
                {t("landing.cases_sub")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <BeforeAfter
                name="Café Lumbre"
                industry="Café especialidad · CDMX"
                beforeImg="https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?q=80&w=800&auto=format&fit=crop"
                beforeLabel="Logo casero · Canva"
                beforeCopy="2% CTR · 340 seguidores"
                afterImg="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?q=80&w=800&auto=format&fit=crop"
                afterLabel="Identidad CIA Marketing®"
                afterCopy="9.4% CTR · 12,800 seguidores (+3,671%)"
              />
              <BeforeAfter
                name="Lummo Mx"
                industry="E-commerce ropa · Guadalajara"
                beforeImg="https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=800&auto=format&fit=crop"
                beforeLabel="Sin guía de marca"
                beforeCopy="ROAS 1.1× · 42 compras/mes"
                afterImg="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?q=80&w=800&auto=format&fit=crop"
                afterLabel="Branding + autopilot"
                afterCopy="ROAS 4.7× · 680 compras/mes (+1,519%)"
              />
              <BeforeAfter
                name="Kairo SaaS"
                industry="B2B software · Medellín"
                beforeImg="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=800&auto=format&fit=crop"
                beforeLabel="Solo landing · sin redes"
                beforeCopy="120 MQLs/mes · CAC $88"
                afterImg="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=800&auto=format&fit=crop"
                afterLabel="Branding + LinkedIn auto"
                afterCopy="480 MQLs/mes · CAC $29 (-67%)"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing — teaser con doble precio */}
      <section id="pricing" className="border-b border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <Mark>{tr("DOSSIER 04 · TARIFAS")}</Mark>
              <h2 className="font-display text-4xl sm:text-5xl mt-3">{tr("Precios sin trampas.")}</h2>
              <p className="font-mono-data text-sm text-[#5C5C5C] mt-4 max-w-lg">
                {tr("Elige tu plan. Añade el módulo de")} <b>Video IA</b> {tr("si lo necesitas — duplica el precio del plan y obtienes hasta 1 video/día.")}
              </p>
            </div>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-6 py-4 font-mono-mark text-xs border border-[#0A0A0A] hover-brutal"
              data-testid="landing-pricing-explorer"
            >
              {tr("ABRIR CALCULADORA DE PRECIOS")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-4 gap-0 border border-[#0A0A0A]">
            {plans.map((p, i) => (
              <PricingCard key={p.id} plan={p} last={i === plans.length - 1} />
            ))}
          </div>
          <p className="mt-6 font-mono-mark text-[10px] text-[#5C5C5C]">
            {tr("* PRECIO CON VIDEO = 2× PRECIO BASE · INCLUYE HASTA 30 VIDEOS/MES · FRECUENCIA CONFIGURABLE")}
          </p>
        </div>
      </section>

      {/* CTA Final */}
      <section className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Mark>{t("landing.cta_final_mark").toUpperCase()}</Mark>
            <h2 className="font-display text-4xl sm:text-5xl mt-3">{t("landing.cta_final_title")} <span className="text-[#FFD600]">{t("landing.cta_final_highlight")}</span></h2>
          </div>
          <div className="flex md:justify-end">
            <Link to="/register" className="inline-flex items-center gap-3 bg-[#E53935] text-white px-6 py-4 font-mono-mark text-xs border border-[#E53935] hover-brutal" data-testid="footer-cta-register">
              {t("landing.cta_final_btn").toUpperCase()} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F4F4F0] border-t border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img src="/logo-cia.png" alt="CIA Marketing" className="w-8 h-8 object-contain" />
            <span className="font-mono-mark text-[11px]">{tr("CIA MARKETING® · 2026 · GLOBAL")}</span>
          </div>
          <div className="flex flex-wrap gap-4 font-mono-mark text-[10px] text-[#5C5C5C]" data-testid="footer-legal">
            <Link to="/legal/terms" className="hover:text-[#0A0A0A]">{tr("TÉRMINOS")}</Link>
            <Link to="/legal/privacy" className="hover:text-[#0A0A0A]">{tr("PRIVACIDAD")}</Link>
            <Link to="/legal/cookies" className="hover:text-[#0A0A0A]">{tr("COOKIES")}</Link>
            <Link to="/legal/dpa" className="hover:text-[#0A0A0A]">DPA</Link>
            <Link to="/support" className="hover:text-[#0A0A0A]" data-testid="footer-support-link">{tr("SOPORTE")}</Link>
            <a href="mailto:legal@ciamkt.com" className="hover:text-[#0A0A0A]">legal@ciamkt.com</a>
          </div>
          <div className="font-mono-data text-xs text-[#5C5C5C] w-full md:w-auto">
            {t("landing.footer_line")}
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, kicker, size = "md", children }) {
  const span = size === "lg" ? "col-span-12 md:col-span-7" : size === "md" ? "col-span-12 md:col-span-5" : "col-span-12 sm:col-span-6 md:col-span-4";
  return (
    <div className={`${span} bg-white border border-[#0A0A0A] p-6 md:p-8 hover-brutal`}>
      <div className="flex items-center justify-between mb-6">
        <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{kicker}</span>
        <div className="w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center">{icon}</div>
      </div>
      <div className="font-display text-2xl md:text-3xl leading-tight">{title}</div>
      {children && <p className="mt-4 text-sm text-[#5C5C5C] font-mono-data">{children}</p>}
    </div>
  );
}

function SmartCard({ tag, icon, title, size = "md", children }) {
  const span =
    size === "lg" ? "col-span-12 md:col-span-7" :
    size === "md" ? "col-span-12 md:col-span-5" :
    "col-span-12 sm:col-span-6 md:col-span-4";
  return (
    <div
      className={`${span} relative bg-[#1a1a1a] border border-white/10 p-6 md:p-8
                  hover:border-[#FFD600] transition-colors duration-300 group`}
    >
      <div className="flex items-center justify-between mb-6">
        <span className="font-mono-mark text-[10px] text-[#FFD600]">{tag}</span>
        <div className="w-9 h-9 bg-[#FFD600] text-[#0A0A0A] flex items-center justify-center
                        group-hover:rotate-3 transition-transform duration-300">{icon}</div>
      </div>
      <div className="font-display text-2xl md:text-3xl leading-tight text-white">{title}</div>
      {children && <p className="mt-4 text-sm text-white/60 font-mono-data leading-relaxed">{children}</p>}
    </div>
  );
}

function PricingCard({ plan, last }) {
  const highlighted = plan.highlight;
  const isFree = plan.id === "free";
  const { format } = useCurrency();
  const tr = useTr();
  return (
    <div
      className={`p-6 md:p-8 flex flex-col ${last ? "" : "border-b md:border-b-0 md:border-r border-[#0A0A0A]"} ${highlighted ? "bg-[#0A0A0A] text-white" : "bg-white"}`}
      data-testid={`pricing-card-${plan.id}`}
    >
      <div className="flex items-center justify-between">
        <div className={`font-mono-mark text-[10px] ${highlighted ? "text-[#FFD600]" : "text-[#5C5C5C]"}`}>{tr(plan.name).toUpperCase()}</div>
        {highlighted && <span className="font-mono-mark text-[9px] bg-[#E53935] text-white px-2 py-0.5">POPULAR</span>}
      </div>

      {/* Base price */}
      <div className="mt-5 flex items-baseline gap-1">
        <span className="font-display text-5xl leading-none">{format(plan.price_usd)}</span>
        <span className="font-mono-mark text-[10px]">/{tr(plan.period)}</span>
      </div>

      {/* Video price row */}
      {!isFree && (
        <div className={`mt-3 border ${highlighted ? "border-[#FFD600]/40 bg-white/5" : "border-[#0A0A0A] bg-[#FFD600]"} p-2`}>
          <div className={`font-mono-mark text-[9px] ${highlighted ? "text-[#FFD600]" : "text-[#0A0A0A]"}`}>{tr("CON VIDEOS IA · 2×")}</div>
          <div className="flex items-baseline gap-1 mt-0.5">
            <span className="font-display text-xl">{format(plan.video_price_usd)}</span>
            <span className="font-mono-mark text-[9px]">/{tr(plan.period)}</span>
          </div>
        </div>
      )}

      <ul className="mt-5 space-y-2 text-sm font-mono-data flex-1">
        {plan.features.slice(0, 5).map((f) => (
          <li key={f} className="flex gap-2">
            <span className="text-[#E53935]">+</span>
            <span>{tr(f)}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/pricing"
        className={`mt-6 inline-block text-center font-mono-mark text-[11px] px-4 py-3 border ${highlighted ? "bg-[#E53935] text-white border-[#E53935] hover:bg-white hover:text-[#0A0A0A]" : "bg-transparent text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"}`}
        data-testid={`pricing-cta-${plan.id}`}
      >
        {tr("VER DETALLES")}
      </Link>
    </div>
  );
}

function VoiceCard({ label, bg, text, tags, stats, dark }) {
  return (
    <div className={`${bg} border border-[#0A0A0A] p-6 flex flex-col`}>
      <div className={`font-mono-mark text-[9px] ${dark ? "text-[#FFD600]" : "text-[#5C5C5C]"}`}>{label}</div>
      <p className="mt-4 font-display text-lg leading-tight flex-1 whitespace-pre-wrap">{text}</p>
      <div className={`mt-4 font-mono-data text-[11px] ${dark ? "text-white/60" : "text-[#0A0A0A]/70"}`}>{tags}</div>
      <div className={`mt-3 pt-3 border-t ${dark ? "border-white/20" : "border-[#0A0A0A]/20"} font-mono-mark text-[9px] ${dark ? "text-[#E53935]" : "text-[#E53935]"}`}>
        {stats}
      </div>
    </div>
  );
}

function BrandIncl({ n, t, d }) {
  return (
    <div className="bg-white/5 backdrop-blur border border-white/20 p-3">
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 bg-[#FFD600] text-[#0A0A0A] font-mono-mark text-[9px] flex items-center justify-center">{n}</span>
        <div className="font-display text-sm text-white">{t}</div>
      </div>
      <div className="font-mono-data text-[10px] text-white/60 mt-1">{d}</div>
    </div>
  );
}

function Cap({ n, cat, t, d, highlight, emph }) {
  const bg = highlight ? "bg-[#E53935] text-white border-[#E53935]" : emph ? "bg-[#FFD600] text-[#0A0A0A] border-[#FFD600]" : "bg-white/5 backdrop-blur border-white/20 text-white";
  const catColor = highlight ? "text-white/80" : emph ? "text-[#0A0A0A]/70" : "text-[#FFD600]";
  const descColor = highlight ? "text-white/80" : emph ? "text-[#0A0A0A]/70" : "text-white/60";
  return (
    <div className={`${bg} border p-4 flex flex-col gap-2 hover-brutal transition`} data-testid={`cap-${n}`}>
      <div className="flex items-start justify-between">
        <span className={`font-mono-mark text-[8px] ${catColor}`}>{cat}</span>
        <span className="font-mono-mark text-[9px] opacity-60">{n}</span>
      </div>
      <div className="font-display text-base leading-tight mt-1">{t}</div>
      <div className={`font-mono-data text-[11px] ${descColor} leading-relaxed flex-1`}>{d}</div>
      {highlight && <div className="font-mono-mark text-[8px] bg-[#FFD600] text-[#0A0A0A] self-start px-2 py-0.5 mt-1">{(cat && cat.includes("NUEVO")) || (cat && cat.includes("NEW")) || (cat && cat.includes("NOVO")) ? cat.split("·").pop().trim() : "NEW"}</div>}
    </div>
  );
}

function BeforeAfter({ name, industry, beforeImg, beforeLabel, beforeCopy, afterImg, afterLabel, afterCopy }) {
  const tr = useTr();
  return (
    <div className="bg-white text-[#0A0A0A] border border-[#0A0A0A] brutal-shadow overflow-hidden" data-testid={`case-${name.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="px-4 py-3 border-b border-[#0A0A0A]">
        <div className="font-display text-lg leading-tight">{name}</div>
        <div className="font-mono-mark text-[9px] text-[#5C5C5C] mt-0.5">{tr(industry)}</div>
      </div>
      <div className="grid grid-cols-2">
        {/* Before */}
        <div className="relative border-r border-[#0A0A0A]">
          <span className="absolute top-2 left-2 font-mono-mark text-[9px] bg-[#5C5C5C] text-white px-2 py-0.5 z-10">{tr("ANTES")}</span>
          <img src={beforeImg} alt="antes" className="w-full aspect-square object-cover grayscale opacity-80" />
          <div className="p-3 bg-[#F4F4F0]">
            <div className="font-display text-sm leading-tight">{tr(beforeLabel)}</div>
            <div className="font-mono-data text-[11px] text-[#5C5C5C] mt-1">{tr(beforeCopy)}</div>
          </div>
        </div>
        {/* After */}
        <div className="relative">
          <span className="absolute top-2 left-2 font-mono-mark text-[9px] bg-[#E53935] text-white px-2 py-0.5 z-10">{tr("DESPUÉS")}</span>
          <img src={afterImg} alt="después" className="w-full aspect-square object-cover" />
          <div className="p-3 bg-[#FFD600]">
            <div className="font-display text-sm leading-tight">{tr(afterLabel)}</div>
            <div className="font-mono-data text-[11px] text-[#0A0A0A] mt-1">{tr(afterCopy)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
